<!--
RTC统计页面组件
被主导航调用
权限点：rtc.stats
简要说明：用于显示实时通信的统计数据和性能指标
-->
<template>
  <div class="rtc-stats">
    <!-- 统计概览卡片 -->
    <div class="stats-overview">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-card class="stat-card">
            <div class="stat-content">
              <div class="stat-icon online">
                <el-icon><Connection /></el-icon>
              </div>
              <div class="stat-info">
                <div class="stat-number">
                  {{ statsOverview.activeConnections }}
                </div>
                <div class="stat-label">活跃连接</div>
              </div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="stat-card">
            <div class="stat-content">
              <div class="stat-icon call">
                <el-icon><Phone /></el-icon>
              </div>
              <div class="stat-info">
                <div class="stat-number">{{ statsOverview.totalCalls }}</div>
                <div class="stat-label">总通话数</div>
              </div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="stat-card">
            <div class="stat-content">
              <div class="stat-icon quality">
                <el-icon><TrendCharts /></el-icon>
              </div>
              <div class="stat-info">
                <div class="stat-number">{{ statsOverview.avgQuality }}%</div>
                <div class="stat-label">平均质量</div>
              </div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="stat-card">
            <div class="stat-content">
              <div class="stat-icon latency">
                <el-icon><Timer /></el-icon>
              </div>
              <div class="stat-info">
                <div class="stat-number">{{ statsOverview.avgLatency }}ms</div>
                <div class="stat-label">平均延迟</div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 详细统计 -->
    <div class="detailed-stats">
      <el-row :gutter="20">
        <!-- 左侧：连接质量趋势 -->
        <el-col :span="12">
          <el-card class="chart-card">
            <template #header>
              <div class="card-header">
                <span>连接质量趋势</span>
                <el-select v-model="qualityTimeRange" style="width: 120px">
                  <el-option label="最近1小时" value="1h" />
                  <el-option label="最近24小时" value="24h" />
                  <el-option label="最近7天" value="7d" />
                </el-select>
              </div>
            </template>
            <div class="chart-container">
              <div class="chart-placeholder">
                <el-icon><TrendCharts /></el-icon>
                <p>连接质量趋势图表</p>
                <small>显示最近时间段的连接质量变化</small>
              </div>
            </div>
          </el-card>
        </el-col>

        <!-- 右侧：设备连接状态 -->
        <el-col :span="12">
          <el-card class="chart-card">
            <template #header>
              <div class="card-header">
                <span>设备连接状态</span>
              </div>
            </template>
            <div class="chart-container">
              <div class="chart-placeholder">
                <el-icon><PieChart /></el-icon>
                <p>设备连接状态分布</p>
                <small>显示在线、离线、通话中等状态的设备数量</small>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 实时连接列表 -->
    <div class="connection-list">
      <el-card class="list-card">
        <template #header>
          <div class="card-header">
            <span>实时连接列表</span>
            <div class="header-actions">
              <el-input
                v-model="searchKeyword"
                placeholder="搜索设备ID"
                style="width: 200px"
                clearable
              >
                <template #prefix>
                  <el-icon><Search /></el-icon>
                </template>
              </el-input>
            </div>
          </div>
        </template>

        <el-table
          :data="filteredConnections"
          style="width: 100%"
          table-layout="auto"
          v-loading="loading"
        >
          <el-table-column prop="device_id" label="设备ID" width="150" />
          <el-table-column prop="connection_id" label="连接ID" width="150" />
          <el-table-column prop="status" label="状态" width="100">
            <template #default="scope">
              <el-tag :type="getConnectionStatusType(scope.row.status)">
                {{ getConnectionStatusText(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="quality" label="质量" width="100">
            <template #default="scope">
              <el-progress
                :percentage="scope.row.quality"
                :color="getQualityColor(scope.row.quality)"
                :stroke-width="8"
              />
            </template>
          </el-table-column>
          <el-table-column prop="latency" label="延迟" width="100">
            <template #default="scope"> {{ scope.row.latency }}ms </template>
          </el-table-column>
          <el-table-column prop="packet_loss" label="丢包率" width="100">
            <template #default="scope"> {{ scope.row.packet_loss }}% </template>
          </el-table-column>
          <el-table-column prop="connected_at" label="连接时间" width="160" />
          <el-table-column label="操作" width="120" fixed="right">
            <template #default="scope">
              <div class="action-buttons">
                <el-button
                  type="primary"
                  size="small"
                  @click="viewConnectionDetails(scope.row)"
                >
                  <el-icon><View /></el-icon>
                  详情
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </div>

    <!-- 连接详情对话框 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="连接详情"
      width="800px"
      @close="closeDetailDialog"
    >
      <div class="connection-details" v-if="currentConnection">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="设备ID">
            {{ currentConnection.device_id }}
          </el-descriptions-item>
          <el-descriptions-item label="连接ID">
            {{ currentConnection.connection_id }}
          </el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getConnectionStatusType(currentConnection.status)">
              {{ getConnectionStatusText(currentConnection.status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="连接时间">
            {{ currentConnection.connected_at }}
          </el-descriptions-item>
          <el-descriptions-item label="质量">
            <el-progress
              :percentage="currentConnection.quality"
              :color="getQualityColor(currentConnection.quality)"
            />
          </el-descriptions-item>
          <el-descriptions-item label="延迟">
            {{ currentConnection.latency }}ms
          </el-descriptions-item>
          <el-descriptions-item label="丢包率">
            {{ currentConnection.packet_loss }}%
          </el-descriptions-item>
          <el-descriptions-item label="带宽">
            {{ currentConnection.bandwidth }}kbps
          </el-descriptions-item>
        </el-descriptions>

        <div class="detail-charts">
          <h4>实时指标</h4>
          <div class="chart-placeholder">
            <el-icon><TrendCharts /></el-icon>
            <p>实时质量、延迟、丢包率趋势图</p>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted, computed } from "vue";
import {
  Refresh,
  Search,
  View,
  Connection,
  Phone,
  TrendCharts,
  Timer,
  PieChart,
} from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { useStore } from "vuex";

// 中文注释：引入全局store
const store = useStore();

// 权限检查函数
const hasPermission = (perm: string) => store.getters.hasPermission(perm);

// 响应式数据
const loading = ref(false);
const detailDialogVisible = ref(false);
const currentConnection = ref(null);
const searchKeyword = ref("");
const qualityTimeRange = ref("1h");

// 统计概览数据
const statsOverview = reactive({
  activeConnections: 5,
  totalCalls: 128,
  avgQuality: 95,
  avgLatency: 45,
});

// 连接列表数据
const connections = ref([
  {
    device_id: "d83bda4d33b8",
    connection_id: "conn_001",
    status: "connected",
    quality: 98,
    latency: 32,
    packet_loss: 0.1,
    connected_at: "2024-01-15 10:30:00",
    bandwidth: 128,
  },
  {
    device_id: "d83bda4d33b9",
    connection_id: "conn_002",
    status: "connecting",
    quality: 85,
    latency: 65,
    packet_loss: 2.3,
    connected_at: "2024-01-15 10:25:00",
    bandwidth: 96,
  },
]);

// 过滤后的连接列表
const filteredConnections = computed(() => {
  if (!searchKeyword.value) {
    return connections.value;
  }
  return connections.value.filter((conn) =>
    conn.device_id.toLowerCase().includes(searchKeyword.value.toLowerCase())
  );
});

// 获取RTC统计数据
const fetchRTCStats = async () => {
  loading.value = true;
  try {
    // TODO: 调用真实API
    console.log("[真实接口] 获取RTC统计数据");
    // const response = await apiClient.get("/rtc/stats");
    // statsOverview.activeConnections = response.data.active_connections;
    // statsOverview.totalCalls = response.data.total_calls;
    // statsOverview.avgQuality = response.data.avg_quality;
    // statsOverview.avgLatency = response.data.avg_latency;
  } catch (error) {
    console.error("获取RTC统计数据失败:", error);
    ElMessage.error("获取RTC统计数据失败");
  } finally {
    loading.value = false;
  }
};

// 获取连接列表
const fetchConnections = async () => {
  loading.value = true;
  try {
    // TODO: 调用真实API
    console.log("[真实接口] 获取连接列表");
    // const response = await apiClient.get("/rtc/connections");
    // connections.value = response.data;
  } catch (error) {
    console.error("获取连接列表失败:", error);
    ElMessage.error("获取连接列表失败");
  } finally {
    loading.value = false;
  }
};

// 刷新统计数据
const refreshStats = () => {
  fetchRTCStats();
  fetchConnections();
};

// 查看连接详情
const viewConnectionDetails = (connection: any) => {
  currentConnection.value = connection;
  detailDialogVisible.value = true;
};

// 关闭详情对话框
const closeDetailDialog = () => {
  detailDialogVisible.value = false;
  currentConnection.value = null;
};

// 获取连接状态类型
const getConnectionStatusType = (status: string) => {
  const statusMap: Record<string, string> = {
    connected: "success",
    connecting: "warning",
    disconnected: "danger",
    error: "danger",
  };
  return statusMap[status] || "info";
};

// 获取连接状态文本
const getConnectionStatusText = (status: string) => {
  const statusMap: Record<string, string> = {
    connected: "已连接",
    connecting: "连接中",
    disconnected: "已断开",
    error: "错误",
  };
  return statusMap[status] || status;
};

// 获取质量颜色
const getQualityColor = (quality: number) => {
  if (quality >= 90) return "#67C23A";
  if (quality >= 70) return "#E6A23C";
  return "#F56C6C";
};

// 页面加载时获取数据
onMounted(() => {
  fetchRTCStats();
  fetchConnections();
});
</script>

<style lang="scss" scoped>
.rtc-stats {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  h2 {
    margin: 0;
    color: #2c3e50;
  }

  .header-actions {
    display: flex;
    gap: 10px;
  }
}

.stats-overview {
  margin-bottom: 20px;

  .stat-card {
    .stat-content {
      display: flex;
      align-items: center;
      gap: 15px;

      .stat-icon {
        width: 50px;
        height: 50px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        color: white;

        &.online {
          background: linear-gradient(135deg, #67c23a, #85ce61);
        }

        &.call {
          background: linear-gradient(135deg, #409eff, #66b1ff);
        }

        &.quality {
          background: linear-gradient(135deg, #e6a23c, #eebe77);
        }

        &.latency {
          background: linear-gradient(135deg, #f56c6c, #f78989);
        }
      }

      .stat-info {
        .stat-number {
          font-size: 24px;
          font-weight: bold;
          color: #2c3e50;
          line-height: 1;
        }

        .stat-label {
          font-size: 14px;
          color: #666;
          margin-top: 5px;
        }
      }
    }
  }
}

.detailed-stats {
  margin-bottom: 20px;

  .chart-card {
    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .chart-container {
      height: 300px;
      display: flex;
      align-items: center;
      justify-content: center;

      .chart-placeholder {
        text-align: center;
        color: #999;

        .el-icon {
          font-size: 48px;
          margin-bottom: 10px;
        }

        p {
          margin: 10px 0 5px;
          font-size: 16px;
        }

        small {
          font-size: 12px;
        }
      }
    }
  }
}

.connection-list {
  .list-card {
    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .action-buttons {
      display: flex;
      gap: 4px;
      align-items: center;
      justify-content: center;
    }
  }
}

.connection-details {
  .detail-charts {
    margin-top: 20px;

    h4 {
      margin-bottom: 15px;
      color: #2c3e50;
    }

    .chart-placeholder {
      height: 200px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #999;
      border: 1px dashed #ddd;
      border-radius: 8px;

      .el-icon {
        font-size: 32px;
        margin-bottom: 10px;
      }

      p {
        margin: 0;
        font-size: 14px;
      }
    }
  }
}
</style>
